/**
 * AuthorizedDomainsManager — an always-available way to see and manage which
 * domains this organization may run automated tests against.
 *
 * Why this exists as its own control (not just the pre-flight card): the agent
 * can refuse an unauthorized domain BEFORE it ever renders a pre-flight card
 * (e.g. it bails to a text question about a suspicious .local host). In that
 * case there was no button to authorize anything. This dialog is reachable from
 * the header at any time, independent of what the agent decides to do.
 *
 * Authorization is an org-scoped admin attestation; non-admins can view the
 * list but the add/revoke actions are disabled for them (and the backend
 * enforces it with 403 regardless).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  Divider, FormControlLabel, Checkbox, IconButton, List, ListItem,
  ListItemText, TextField, Tooltip, Typography, Alert,
} from '@mui/material';
import {
  Security as SecurityIcon, Delete as DeleteIcon, Add as AddIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import {
  listAuthorizedDomains, authorizeDomain, revokeDomain, AuthorizedDomain,
} from '../services/project.service';

interface Props {
  projectId?: string | number | null;
  canManage?: boolean; // org admin — may add/revoke
}

const normalizeDomain = (raw: string): string => {
  let d = raw.trim().toLowerCase();
  d = d.replace(/^https?:\/\//, '');
  d = d.split('/')[0].split(':')[0];
  return d;
};

const AuthorizedDomainsManager: React.FC<Props> = ({ projectId, canManage = false }) => {
  const [open, setOpen] = useState(false);
  const [domains, setDomains] = useState<AuthorizedDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newDomain, setNewDomain] = useState('');
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    listAuthorizedDomains(String(projectId))
      .then((d) => setDomains(d.filter((x) => x.testing_authorized)))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const handleAuthorize = async () => {
    const domain = normalizeDomain(newDomain);
    if (!domain || !attested || !projectId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await authorizeDomain(String(projectId), domain);
      setNewDomain('');
      setAttested(false);
      refresh();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (domain: string) => {
    if (!projectId) return;
    setError(null);
    try {
      await revokeDomain(String(projectId), domain);
      refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (!projectId) return null;

  return (
    <>
      <Tooltip title="Authorized testing domains">
        <Button
          size="small"
          variant="outlined"
          startIcon={<SecurityIcon />}
          onClick={() => setOpen(true)}
          sx={{ ml: 1, textTransform: 'none' }}
        >
          Domains
        </Button>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon fontSize="small" />
            <span>Authorized testing domains</span>
          </Box>
          <Typography variant="caption" color="text.secondary">
            The agent will only crawl and test domains authorized here. Scope is
            your whole organization.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

          {/* Add / authorize (admins only) */}
          {canManage ? (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth size="small" placeholder="example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAuthorize(); }}
                />
                <Button
                  variant="contained" size="small"
                  startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
                  disabled={!newDomain.trim() || !attested || submitting}
                  onClick={handleAuthorize}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Authorize
                </Button>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small" checked={attested}
                    onChange={(e) => setAttested(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="caption">
                    I confirm my organization owns or is permitted to run automated
                    tests against this domain.
                  </Typography>
                }
                sx={{ alignItems: 'flex-start', mt: 0.5 }}
              />
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Only an organization admin can authorize or revoke domains.
            </Alert>
          )}

          <Divider sx={{ mb: 1 }} />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : domains.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No domains authorized yet.
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
              {domains.map((d) => (
                <ListItem
                  key={d.domain}
                  secondaryAction={
                    canManage ? (
                      <Tooltip title="Revoke">
                        <IconButton edge="end" size="small" onClick={() => handleRevoke(d.domain)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : undefined
                  }
                >
                  <PublicIcon sx={{ fontSize: '1rem', mr: 1, color: 'text.disabled' }} />
                  <ListItemText
                    primary={d.domain}
                    primaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                    secondary={d.authorized_by ? `by ${d.authorized_by}` : undefined}
                  />
                  <Chip label="Authorized" size="small" color="success" variant="outlined" sx={{ mr: 1 }} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuthorizedDomainsManager;
